// ConsiderSupportInnovationComponent.js
import React, { useEffect, useMemo, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Spinner,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Alert,
    Collapse
} from "reactstrap";
import {
    LuBuilding2,
    LuUserCheck,
    LuMapPin,
    LuPhone,
    LuBookOpen,
    LuUsers,
    LuClock3,
    LuChevronDown,
    LuChevronRight,
    LuEye
} from "react-icons/lu";
// import "bootstrap/dist/css/bootstrap.min.css";
import { FaUserCheck, FaTimesCircle } from "react-icons/fa";


const F = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const SectionHeader = ({ icon: Icon, title, right }) => (
    <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
            <div className="icon-chip">
                <Icon size={18} />
            </div>
            <h6 className="m-0 fw-semibold">{title}</h6>
        </div>
        {right}
    </div>
);

const InfoRow = ({ label, value }) => (
    <Row className="mb-2 align-items-center">
        <Col md="4" className="text-muted small">{label}</Col>
        <Col md="8" className="fw-semibold">{F(value)}</Col>
    </Row>
);

const AddressBlock = ({ a }) => (
    <div className="mt-2">
        <InfoRow label="บ้านเลขที่" value={a?.addressNo} />
        <InfoRow label="หมู่" value={a?.moo} />
        <InfoRow label="ซอย" value={a?.soi} />
        <InfoRow label="ถนน" value={a?.street} />
        <InfoRow label="จังหวัด" value={a?.provinceName} />
        <InfoRow label="อำเภอ" value={a?.amphurName} />
        <InfoRow label="ตำบล" value={a?.tumbonName} />
        {a?.postCode && <InfoRow label="รหัสไปรษณีย์" value={a?.postCode} />}
    </div>
);

const ContactBlock = ({ c }) => (
    <div className="mt-2">
        <InfoRow label="ผู้ประสานงาน" value={c?.coordiNateName} />
        <InfoRow label="เบอร์โทร" value={c?.coordiNatePhone} />
        <InfoRow label="มือถือ" value={c?.coordiNateMobile} />
        <InfoRow label="โทรสาร" value={c?.coordiNateFax} />
        <InfoRow label="อีเมล" value={c?.coordiNateEmail} />
    </div>
);

const TraineeTable = ({ list }) => (
    <div className="soft-card p-0 overflow-hidden">
        <Table responsive hover borderless className="mb-0 align-middle">
            <thead className="table-light">
                <tr>
                    <th style={{ width: 60 }} className="text-center">#</th>
                    <th>ชื่อ-สกุล</th>
                    <th>เลขบัตร</th>
                    <th>ตำแหน่งงาน</th>
                    <th>หมายเหตุ</th>
                </tr>
            </thead>
            <tbody>
                {(list && list.length > 0 ? list : []).map((t, idx) => (
                    <tr key={idx}>
                        <td className="text-center text-muted">{idx + 1}</td>
                        <td className="fw-semibold">{F(t.fullName)}</td>
                        <td className="font-monospace">{F(t.personalNo)}</td>
                        <td>{F(t.jobPosition)}</td>
                        <td className="text-muted">{F(t.remark)}</td>
                    </tr>
                ))}
                {(!list || list.length === 0) && (
                    <tr>
                        <td colSpan="5" className="text-center text-muted py-4">ไม่มีรายชื่อผู้เข้าอบรม</td>
                    </tr>
                )}
            </tbody>
        </Table>
    </div>
);

/** --------------------------
 *  Class item (collapsible)
 *  -------------------------*/
const ClassDetailItem = ({ d, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    const [openTrainee, setOpenTrainee] = useState(false);

    // สรุปข้อมูลสั้น ๆ ที่โชว์ตอนปิด
    const summary = useMemo(() => {
        const date = `${F(d.startDate)} — ${F(d.endDate)}`;
        const loc = F(d.locationName);
        return `${date} · ${loc}`;
    }, [d]);

    return (
        <div className="timeline-item">
            <div className="timeline-dot" />
            <Card className="mb-3 border-0 soft-card">
                <CardHeader
                    className="bg-transparent border-0 py-2"
                    onClick={() => setOpen((v) => !v)}
                    style={{ cursor: "pointer" }}
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                            <span className="caret">
                                {open ? <LuChevronDown /> : <LuChevronRight />}
                            </span>
                            <div className="d-flex flex-column">
                                <div className="fw-semibold">รุ่นที่ {F(d.classId)}</div>
                                <div className="text-muted small">{summary}</div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <span className="chip"><LuUsers size={14} /> {F(d.personTotal)} คน</span>
                            <span className="chip"><LuClock3 size={14} /> {F(d.hour)} ชม.</span>
                        </div>
                    </div>
                </CardHeader>

                <Collapse isOpen={open}>
                    <CardBody className="pt-0">
                        <Row className="g-4">
                            <Col md="6">
                                <div className="subtle-block">
                                    <div className="mb-2 text-muted small">กำหนดการ</div>
                                    <InfoRow label="วันที่เริ่ม" value={d.startDate} />
                                    <InfoRow label="วันที่สิ้นสุด" value={d.endDate} />
                                    <InfoRow label="สถานที่" value={d.locationName} />
                                    <InfoRow label="รูปแบบ" value={d.inOutTypeName} />
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="subtle-block">
                                    <div className="mb-2 text-muted small">ที่อยู่สถานที่ฝึก</div>
                                    <AddressBlock a={d} />
                                </div>
                            </Col>
                        </Row>

                        <Row className="g-4 mt-1">
                            <Col md="6">
                                <div className="subtle-block">
                                    <div className="mb-2 text-muted small">ค่าใช้จ่ายวิทยากร/วัสดุ</div>
                                    <InfoRow label="อัตราค่าวิทยากร (บาท/ชม.)" value={d.tutorRate} />
                                    <InfoRow label="ชั่วโมงวิทยากร (ชม.)" value={d.tutorHourAmount} />
                                    <InfoRow label="วัสดุต่อคน (บาท)" value={d.materialPersonAmount} />
                                    <InfoRow label="ค่าวัสดุรวม (บาท)" value={d.materialAmount} />
                                    <InfoRow label="ค่าใช้จ่ายต่อคน (บาท)" value={d.personAmount} />
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="subtle-block">
                                    <div className="mb-2 text-muted small">การติดต่อ</div>
                                    <InfoRow label="โทรศัพท์" value={d.phone} />
                                    <InfoRow label="มือถือ" value={d.mobile} />
                                    <InfoRow label="แฟกซ์" value={d.fax} />
                                    <InfoRow label="อีเมล" value={d.email} />
                                </div>
                            </Col>
                        </Row>

                        <div className="mt-3">
                            <Button
                                size="sm"
                                color="light"
                                className="btn-chip"
                                onClick={() => setOpenTrainee((v) => !v)}
                            >
                                <LuEye size={16} />
                                <span className="ms-1">รายชื่อผู้เข้าอบรม</span>
                                <span className="ms-1">{openTrainee ? "(ซ่อน)" : "(ดู)"}</span>
                            </Button>
                            <Collapse isOpen={openTrainee}>
                                <div className="mt-3">
                                    <TraineeTable list={d.innoTrainViewModel} />
                                </div>
                            </Collapse>
                        </div>
                    </CardBody>
                </Collapse>
            </Card>
        </div>
    );
};

/** --------------------------
 *  Course card (with Expand All)
 *  -------------------------*/
const CourseCard = ({ c }) => {
    const details = c?.detailInnoCourse || [];
    const [allOpen, setAllOpen] = useState(false);

    // ถ้าคลาสเยอะ (>5) โชว์ปุ่ม Expand/Collapse all
    const showBulkToggle = details.length > 5;

    return (
        <div className="timeline">
            <Card className="mb-3 border-0 soft-card">
                <CardHeader className="bg-transparent border-0 pb-0">
                    <SectionHeader
                        icon={LuBookOpen}
                        title="ข้อมูลหลักสูตรการฝึกอบรม"
                        right={
                            <div className="d-flex align-items-center gap-2">
                                <span className="chip">จำนวน {F(c.classAmount)} รุ่น</span>
                                {showBulkToggle && (
                                    <Button
                                        size="sm"
                                        color="light"
                                        className="btn-chip"
                                        onClick={() => setAllOpen((v) => !v)}
                                    >
                                        {allOpen ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
                                        <span className="ms-1">{allOpen ? "ย่อทั้งหมด" : "ขยายทั้งหมด"}</span>
                                    </Button>
                                )}
                            </div>
                        }
                    />
                </CardHeader>
                <CardBody className="pt-3">
                    {details.length > 0 ? (
                        details.map((d, idx) => (
                            <ClassDetailItem
                                key={d.detailInnoCourseId || idx}
                                d={d}
                                defaultOpen={allOpen}   // sync open state แบบง่าย: เปลี่ยนค่า default เมื่อ allOpen เปลี่ยน
                            />
                        ))
                    ) : (
                        <div className="text-center text-muted py-4">ไม่มีรายละเอียดคลาส</div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export function ConsiderSupportInnovationComponent() {
    const user = JSON.parse(localStorage.getItem("userData"));
    const [data, setData] = useState(null);
    console.log("data", data);
    const [loading, setLoading] = useState(true);    // แถบโหลด
    const [error, setError] = useState("");

    const [actionLoading, setActionLoading] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [actionMsg, setActionMsg] = useState({ type: "", text: "" });

    useEffect(() => {
        getSupportInnovation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSupportInnovation = async () => {
        setLoading(true);
        setError("");
        try {
            const postData = new FormData();
            postData.append("supportInnovationId", Number(1222));

            const res = await fetch("/api/SupportInnovation/getSupportInnovationDetail", {
                method: "POST",
                body: postData,
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const resJson = await res.json();
            setData(resJson?.dataenum || null);
        } catch (e) {
            setError("ไม่สามารถดึงข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!data?.supportInnovationId) return;
        setActionLoading(true);
        setActionMsg({ type: "", text: "" });
        try {
            // const postData = new FormData();
            // postData.append("supportInnovationId", data.supportInnovationId);
            // postData.append("supportInnovationstatusId", "21");
            // postData.append("userApprove", user.fullName || "");
            const res = await fetch("/api/Support/approveOrRejectForInnovation", {
                method: "POST",
                headers: { "Content-type": "application/json" },
                body: JSON.stringify({
                    SupportInnovationId: String(data.supportInnovationId),
                    userApprove: user.fullName,
                    supportInnovationstatusId: "21",

                }),

                // body: postData,
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const resJson = await res.json();

            setActionMsg({ type: "success", text: resJson?.message || "อนุมัติสำเร็จ" });
            // ถ้าต้องการ refresh รายการ:
            await getSupportInnovation();
        } catch (e) {
            setActionMsg({ type: "danger", text: "อนุมัติไม่สำเร็จ กรุณาลองใหม่" });
        } finally {
            setActionLoading(false);
        }
    };

    const toggleReject = () => setRejectOpen((v) => !v);

    const submitReject = async () => {
        if (!data?.supportInnovationId) return;
        setActionLoading(true);
        setActionMsg({ type: "", text: "" });
        try {
            const postData = new FormData();
            postData.append("supportInnovationId", data.supportInnovationId);
            postData.append("RemarkOfficer", rejectReason || "");
            postData.append("userApprove", user.fullName || "");


            const res = await fetch("/api/Support/approveOrRejectForInnovation", {
                method: "POST",
                headers: { "Content-type": "application/json" },
                body: JSON.stringify({
                    SupportInnovationId: data.supportInnovationId,
                    RemarkOfficer: rejectReason,
                    userApprove: user.fullName,
                    supportInnovationstatusId: "22",

                }),

                // body: postData,
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const resJson = await res.json();

            setActionMsg({ type: "success", text: resJson?.message || "ดำเนินการไม่อนุมัติสำเร็จ" });
            setRejectReason("");
            setRejectOpen(false);
            // ถ้าต้องการ refresh รายการ:
            await getSupportInnovation();
        } catch (e) {
            setActionMsg({ type: "danger", text: "ดำเนินการไม่อนุมัติไม่สำเร็จ กรุณาลองใหม่" });
        } finally {
            setActionLoading(false);
        }
    };


    return (

        <Container className="py-4 modern-container">
            <Modal isOpen={rejectOpen} toggle={toggleReject}>
                <ModalHeader toggle={toggleReject}>ระบุเหตุผลการไม่อนุมัติ</ModalHeader>
                <ModalBody>
                    <div className="mb-2 text-muted small">
                        กรุณาระบุเหตุผลเพื่อแจ้งกลับไปยังผู้ยื่นคำขอ
                    </div>
                    <Input
                        type="textarea"
                        rows="5"
                        placeholder="พิมพ์เหตุผลการไม่อนุมัติ..."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleReject} disabled={actionLoading}>
                        ยกเลิก
                    </Button>
                    <Button color="danger" onClick={submitReject} disabled={actionLoading}>
                        {actionLoading ? "กำลังส่ง..." : "ยืนยันไม่อนุมัติ"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Row className="mb-3">
                <Col>
                    <div className="page-title soft-card border-0 d-flex align-items-center gap-3">
                        <div className="title-icon">
                            <LuBuilding2 size={22} />
                        </div>
                        <div>
                            <h5 className="m-0 fw-bold">ข้อมูลคำขอรับความเห็นชอบหลักสูตรการฝึกอบรมเพื่อสร้างสรรค์นวัตกรรม</h5>
                            <div className="text-muted small">รายละเอียดคำขอและข้อมูลหลักสูตรการฝึกอบรม</div>
                        </div>
                        <div className="ms-auto d-none d-md-flex gap-2">
                            {data?.employeeAmount && (
                                <span className="chip">จำนวนพนักงาน {F(data?.employeeAmount)} คน</span>
                            )}
                            {data?.customerNo && (
                                <span className="chip">เลขทะเบียนนิติบุคคล: {F(data?.customerNo)}</span>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {loading && (
                <div className="soft-card d-flex align-items-center justify-content-center py-5 mb-4">
                    <Spinner /> <span className="ms-2">กำลังโหลดข้อมูล...</span>
                </div>
            )}

            {error && (
                <div className="soft-card text-danger fw-semibold p-3 mb-4">
                    {error}
                </div>
            )}
            {actionMsg.text && (
                <Alert color={actionMsg.type} className="soft-card p-3 mb-4">
                    {actionMsg.text}
                </Alert>
            )}

            {!loading && !error && (
                <>
                    <Card className="mb-4 border-0 soft-card">
                        <CardHeader className="bg-transparent border-0 pb-0">
                            <SectionHeader icon={LuUserCheck} title="ข้อมูลสถานประกอบการ" />
                        </CardHeader>
                        <CardBody>
                            <Row className="g-4">
                                <Col md="6">
                                    <div className="subtle-block">
                                        <InfoRow label="รหัสคำขอ" value={data?.supportInnovationId} />
                                        <InfoRow label="ชื่อนิติบุคคล/กิจการ" value={data?.supportName} />
                                        <InfoRow label="เลขลูกค้า (Customer No)" value={data?.customerNo} />
                                        <InfoRow label="ประเภทธุรกิจ" value={data?.businessType} />
                                        <InfoRow label="จำนวนลูกจ้าง" value={data?.employeeAmount} />
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="subtle-block">
                                        <div className="mb-2 text-muted small">ผู้มีอำนาจลงนาม</div>
                                        <InfoRow label="คำนำหน้า" value={data?.authoriseTitleName} />
                                        <InfoRow label="ชื่อ-สกุล" value={data?.authoriseName} />
                                        <InfoRow label="เลขบัตรประชาชน" value={data?.authorisePersonId} />
                                    </div>
                                </Col>
                            </Row>

                            <Row className="g-4 mt-1">
                                <Col md="6">
                                    <div className="subtle-block">
                                        <SectionHeader icon={LuMapPin} title="ที่อยู่สถานประกอบการ" />
                                        <AddressBlock a={data} />
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="subtle-block">
                                        <SectionHeader icon={LuPhone} title="ข้อมูลการติดต่อ" />
                                        <ContactBlock c={data} />
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {(data?.supportInnovationCourse || []).map((c, idx) => (
                        <CourseCard key={c.innovationCourseId || idx} c={c} />
                    ))}

                    {(!data?.supportInnovationCourse || data.supportInnovationCourse.length === 0) && (
                        <div className="soft-card text-center text-muted py-4">
                            ไม่พบคอร์สฝึกอบรม
                        </div>
                    )}
                    <Card className="border-0 soft-card mt-3">
                        <CardBody className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                            <button disabled={actionLoading || !data?.supportInnovationId} className="btn btn-modern btn-success d-flex align-items-center gap-2" onClick={handleApprove}>
                                <FaUserCheck /> อนุมัติ
                            </button>
                            <button disabled={actionLoading || !data?.supportInnovationId} className="btn btn-modern btn-danger d-flex align-items-center gap-2" onClick={toggleReject}  >
                                <FaTimesCircle /> ไม่อนุมัติ
                            </button>
                            {/* <Button
                                color="success"
                                disabled={actionLoading || !data?.supportInnovationId}
                                onClick={handleApprove}
                            >
                                {actionLoading ? "กำลังดำเนินการ..." : "อนุมัติ"}
                            </Button>
                            <Button
                                color="danger"
                                outline
                                disabled={actionLoading || !data?.supportInnovationId}
                                onClick={toggleReject}
                            >
                                ไม่อนุมัติ
                            </Button> */}
                        </CardBody>
                    </Card>

                </>
            )}

            {/* ---- Minimal styles for “modern + collapsible” look ---- */}
            <style>{`
        .modern-container { max-width: 1100px; }
        .soft-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.06);
          padding: 16px;
        }
        .page-title {
          padding: 16px 20px;
          background: linear-gradient(180deg, rgba(248,249,250,0.8), #fff);
        }
        .title-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          background: linear-gradient(135deg, #eff6ff, #f8fafc);
          color: #0d6efd;
        }
        .icon-chip {
          width: 30px;
          height: 30px;
          border-radius: 10px;
          display: grid;
          place-items: center;
          background: #f4f6f8;
          color: #6c757d;
        }
        .chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          padding: 6px 10px;
          border-radius: 999px;
          background: #f1f5ff;
          color: #0d6efd;
          border: 1px solid rgba(13,110,253,0.15);
        }
        .btn-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border-radius: 999px;
          border: 1px solid #e7eaf0;
          background: #fff;
        }
        .subtle-block {
          background: #fafbfc;
          border: 1px solid #f1f3f5;
          border-radius: 12px;
          padding: 14px;
        }
        .caret {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          display: grid;
          place-items: center;
          background: #f4f6f8;
          color: #6c757d;
        }
        /* Timeline */
        .timeline { position: relative; }
        .timeline:before {
          content: "";
          position: absolute;
          left: 15px;
          top: 12px;
          bottom: 12px;
          width: 2px;
          background: linear-gradient(180deg, #e9ecef, #dee2e6);
        }
        .timeline-item { position: relative; padding-left: 36px; }
        .timeline-dot {
          position: absolute;
          left: 7px;
          top: 18px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #0d6efd;
          box-shadow: 0 0 0 4px rgba(13,110,253,0.12);
        }
        @media (max-width: 576px) {
          .timeline:before { left: 10px; }
          .timeline-item { padding-left: 28px; }
          .timeline-dot { left: 3px; }
        }
      `}</style>
        </Container>
    );
}
